import React, { useMemo } from "react";
import { useAuditSummary } from "@moonscape/shared";
import { Skeleton } from "@/components/ui/skeleton";
import { Callout, DHead, Tag, TagTone } from "@/components/ms";
import { FigLink, HeadLink } from "./chrome";

interface TaxTask {
  id: string;
  title: string;
  description: string;
  count: number;
  priority: "must_fix" | "should_fix" | "informational";
  action: string;
  href: string;
}

const PRIORITY: Record<TaxTask["priority"], { tone: TagTone; label: string }> = {
  must_fix: { tone: "neg", label: "Must fix" },
  should_fix: { tone: "warn", label: "Should fix" },
  informational: { tone: "neu", label: "Review" },
};

export const TaxTasksWidget = () => {
  const { data: auditSummaryData, isLoading } = useAuditSummary();
  const auditSummary = auditSummaryData?.data?.summary;
  const errorTypes = auditSummaryData?.data?.errorTypes || [];

  // Build task list from audit error types
  const tasks: TaxTask[] = useMemo(() => {
    if (!auditSummary || !errorTypes) return [];

    const taskList: TaxTask[] = [];

    // Map error types to user-friendly tasks
    const errorTypeMap: Record<string, Partial<TaxTask>> = {
      missing_cost_basis: {
        id: "missing_cost_basis",
        title: "Missing cost basis",
        description: "Add purchase prices to calculate accurate gains and losses",
        action: "Add Cost Basis",
        href: "/audit?filter=missing_cost_basis",
      },
      incorrect_category: {
        id: "incorrect_category",
        title: "Review transaction categories",
        description: "Verify transaction types for accurate tax treatment",
        action: "Review Categories",
        href: "/audit?filter=incorrect_category",
      },
      self_transfer: {
        id: "self_transfer",
        title: "Confirm self-transfers",
        description: "Mark wallet-to-wallet transfers to avoid double taxation",
        action: "Review Transfers",
        href: "/audit?filter=self_transfer",
      },
      wash_sale_pattern: {
        id: "wash_sale_pattern",
        title: "Potential wash sales",
        description: "Similar asset bought within 30 days of sale",
        action: "Review Pattern",
        href: "/audit?filter=wash_sale",
      },
    };

    // Build tasks from error types
    errorTypes.forEach((error) => {
      const taskTemplate = errorTypeMap[error.errorType];
      if (taskTemplate) {
        taskList.push({
          ...taskTemplate,
          count: error.count,
          priority: error.priority as any,
        } as TaxTask);
      }
    });

    // Sort by priority (must_fix first)
    taskList.sort((a, b) => {
      const priorityOrder = { must_fix: 0, should_fix: 1, informational: 2 };
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    });

    return taskList;
  }, [auditSummary, errorTypes]);

  // Loading state
  if (isLoading) {
    return (
      <section className="flex flex-col">
        <DHead label="Tax tasks" />
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-3 border-t border-ms-line py-[11px] first:border-t-0">
            <Skeleton className="h-4 flex-1" />
            <Skeleton className="h-4 w-16" />
          </div>
        ))}
      </section>
    );
  }

  // No tasks state
  if (tasks.length === 0) {
    return (
      <section className="flex flex-col">
        <DHead label="Tax tasks" />
        <Callout className="mt-3.5">
          <b>All clear.</b> Nothing is holding your number back right now.
        </Callout>
      </section>
    );
  }

  // Calculate summary counts
  const mustFixCount = tasks.filter((t) => t.priority === "must_fix").length;
  const totalIssues = tasks.reduce((sum, t) => sum + t.count, 0);

  return (
    <section className="flex flex-col">
      <DHead label="Tax tasks" right={<HeadLink to="/audit">View all</HeadLink>} />

      <Callout tone="tan" className="mt-3.5">
        <b>
          {tasks.length} question{tasks.length !== 1 ? "s" : ""} are holding your number
          back.
        </b>{" "}
        {totalIssues} transaction{totalIssues !== 1 ? "s" : ""} need review
        {mustFixCount > 0 ? `, ${mustFixCount} of them critical` : ""}.
      </Callout>

      <div className="mt-3.5 flex flex-col">
        {tasks.slice(0, 5).map((task) => (
          <FigLink
            key={task.id}
            to={task.href}
            label={task.title}
            sub={task.description}
            tag={<Tag tone={PRIORITY[task.priority].tone}>{PRIORITY[task.priority].label}</Tag>}
            value={task.count}
          />
        ))}
      </div>

      {tasks.length > 5 && (
        <HeadLink to="/audit" className="mt-3.5 inline-flex min-h-[40px] items-center">
          {tasks.length - 5} more task{tasks.length - 5 !== 1 ? "s" : ""}
        </HeadLink>
      )}
    </section>
  );
};
